// (stesso percorso del tuo Layout attuale)
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { User } from "@/api/entities";
import { createPageUrl } from "@/utils";
import {
  Home,
  BookOpen,
  TrendingUp,
  Settings,
  ClipboardList,
  Menu,
  Calculator,
  History,          // ⬅️ icona per “Rileggi Esercizi”
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { isAdminCached } from "@/lib/admin"; // ⬅️ legge la chiave admin dal localStorage

// Costruisce il menu dinamicamente in base al ruolo
function buildNavItems(isAdmin) {
  const base = [
    { title: "Dashboard",   url: createPageUrl("Dashboard"),   icon: Home },
    { title: "Lezioni",     url: createPageUrl("Lessons"),     icon: BookOpen },

    // ⬇️ FIX: link diretto, evita il fallback di createPageUrl
    { title: "Rileggi Esercizi", url: "/exercise-history",     icon: History },

    { title: "Calcolatore", url: createPageUrl("Calculator"),  icon: Calculator },
    { title: "Progresso",   url: createPageUrl("Progress"),    icon: TrendingUp },
    { title: "Impostazioni",url: createPageUrl("Impostazioni"),icon: Settings },
  ];

  // Solo admin: “Gestisci Contenuto”
  if (isAdmin) {
    base.push({
      title: "Gestisci Contenuto",
      url: createPageUrl("AdminLessons"),
      icon: ClipboardList,
    });
  }

  return base;
}

export default function Layout({ children, currentPageName }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(isAdminCached());

  // Ricalcola il ruolo se cambia la chiave in un’altra tab
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "RB_ADMIN_KEY") setIsAdmin(isAdminCached());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    const checkSetupStatus = async () => {
      const setupPath = createPageUrl("Setup");
      const isSetupRoute = location.pathname.includes(setupPath);

      // ✅ FIX: /tutorial NON deve mai essere bloccato dal guard del setup
      const isTutorialRoute = location.pathname.startsWith("/tutorial");

      // Rotte admin: non imponiamo il setup dell’utente
      const isAdminRoute =
        /\/admin/i.test(location.pathname) ||
        location.pathname === createPageUrl("AdminLessons");

      // ✅ FIX: escludi anche tutorial dal controllo setup
      if (isSetupRoute || isTutorialRoute || isAdminRoute) {
        setIsLoading(false);
        return;
      }

      try {
        const currentUser = await User.me();
        if (!currentUser.setup_completed) {
          // ✅ FIX: usa replace per evitare loop nello storico (back/forward)
          navigate(setupPath, { replace: true });
        } else {
          setIsLoading(false);
        }
      } catch {
        // Utente non loggato o errore: mostriamo comunque la pagina
        setIsLoading(false);
      }
    };
    checkSetupStatus();
  }, [location.pathname, navigate]);

  // ✅ Nessun layout attorno a Setup e Tutorial (evita interferenze/loop)
  if (
    location.pathname.includes(createPageUrl("Setup")) ||
    location.pathname.startsWith("/tutorial")
  ) {
    return children;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const navigationItems = buildNavItems(isAdmin);

  return (
    <div className="min-h-screen">
      <style>{`
        :root {
          --primary-gradient: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          --secondary-gradient: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
          --soft-gradient: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
          --bg-gradient: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
        }
        body {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          background: var(--bg-gradient);
          min-height: 100vh;
        }
      `}</style>

      <div className="flex h-screen">
        {/* Sidebar Desktop */}
        <aside className="hidden md:flex w-64 bg-white/95 backdrop-blur-xl border-r border-white/20 shadow-2xl flex-col">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  RespirareBene
                </h2>
                <p className="text-xs text-gray-500 font-medium">Impara & Cresci</p>
              </div>
            </div>
          </div>

          <div className="p-4 flex-1">
            <nav className="space-y-2">
              {navigationItems.map((item) => (
                <Link
                  key={item.title}
                  to={item.url}
                  className={`flex items-center gap-3 px-4 py-3 font-medium rounded-xl transition-all duration-300 hover:scale-105 ${
                    location.pathname === item.url
                      ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg"
                      : "hover:bg-blue-50 hover:text-blue-700 text-gray-700"
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.title}</span>
                </Link>
              ))}
            </nav>
          </div>
        </aside>

        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-50 md:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <aside
              id="app-drawer" /* ⬅️ per aria-controls */
              className="w-64 h-full bg-white/95 backdrop-blur-xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                    <BookOpen className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                      RespirareBene
                    </h2>
                    <p className="text-xs text-gray-500 font-medium">Impara & Cresci</p>
                  </div>
                </div>
              </div>

              <div className="p-4">
                <nav className="space-y-2">
                  {navigationItems.map((item) => (
                    <Link
                      key={item.title}
                      to={item.url}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 font-medium rounded-xl transition-all duration-300 ${
                        location.pathname === item.url
                          ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg"
                          : "hover:bg-blue-50 hover:text-blue-700 text-gray-700"
                      }`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span>{item.title}</span>
                    </Link>
                  ))}
                </nav>
              </div>
            </aside>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 flex flex-col bg-gradient-to-br from-slate-50 to-blue-50 overflow-hidden">
          {/* Mobile Header */}
          <header className="bg-white/80 backdrop-blur-xl border-b border-gray-100 px-6 py-4 md:hidden shadow-sm">
            <div className="flex items-center gap-4">
              {/* === HAMBURGER BUTTON (tap affidabile) === */}
              <button
                type="button"
                aria-label="Apri menu"
                aria-controls="app-drawer"
                aria-expanded={sidebarOpen ? "true" : "false"}
                onPointerUp={() => setSidebarOpen((v) => !v)}
                onClick={() => setSidebarOpen((v) => !v)}
                className="
                  relative z-50 -ml-1
                  flex items-center justify-center
                  rounded-xl p-2 md:p-3
                  min-w-[44px] min-h-[44px]
                  touch-manipulation
                  active:scale-[0.98]
                  focus:outline-none focus:ring-2 focus:ring-blue-500
                  hover:bg-gray-100 transition-colors duration-200
                "
              >
                <Menu className="w-6 h-6 pointer-events-none" />
              </button>

              <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                RespirareBene
              </h1>
            </div>
          </header>

          <div className="flex-1 overflow-auto">{children}</div>
        </main>
      </div>
    </div>
  );
}
